import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { translations } from './translations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STANDALONE = process.env.REACT_APP_STANDALONE === 'true';
// Standalone-App: Admin-Overrides werden beim Build als Snapshot eingebacken.
// Relativer Pfad, damit es auch unter file:// (USB-Bundle) auflösbar bleibt.
const CONTENT_URL = STANDALONE ? 'content-snapshot.json' : `${API}/content`;

// Deep-merge admin content overrides into the German translations.
// Arrays from overrides REPLACE the base arrays (enables reordering).
function mergeContent(base, ov) {
  if (Array.isArray(ov)) return ov;
  if (ov && typeof ov === 'object') {
    const out = { ...(base && typeof base === 'object' ? base : {}) };
    Object.keys(ov).forEach((k) => {
      out[k] = mergeContent(out[k], ov[k]);
    });
    return out;
  }
  if (typeof ov === 'string') return ov.length ? ov : base;
  return ov == null ? base : ov;
}

export const AVAILABLE_LANGS = [
  { code: 'de', label: 'Deutsch',        flag: '🇩🇪', short: 'DE' },
  { code: 'en', label: 'English',        flag: '🇬🇧', short: 'EN' },
  { code: 'fr', label: 'Français',       flag: '🇫🇷', short: 'FR' },
  { code: 'es', label: 'Español',        flag: '🇪🇸', short: 'ES' },
  { code: 'pt', label: 'Português',      flag: '🇵🇹', short: 'PT' },
  { code: 'it', label: 'Italiano',       flag: '🇮🇹', short: 'IT' },
  { code: 'nl', label: 'Nederlands',     flag: '🇳🇱', short: 'NL' },
  { code: 'sv', label: 'Svenska',        flag: '🇸🇪', short: 'SV' },
  { code: 'pl', label: 'Polski',         flag: '🇵🇱', short: 'PL' },
  { code: 'cs', label: 'Čeština',        flag: '🇨🇿', short: 'CS' },
  { code: 'sk', label: 'Slovenčina',     flag: '🇸🇰', short: 'SK' },
  { code: 'hu', label: 'Magyar',         flag: '🇭🇺', short: 'HU' },
  { code: 'ro', label: 'Română',         flag: '🇷🇴', short: 'RO' },
  { code: 'bg', label: 'Български',      flag: '🇧🇬', short: 'BG' },
  { code: 'el', label: 'Ελληνικά',       flag: '🇬🇷', short: 'EL' },
  { code: 'sr', label: 'BKS',            flag: '🇷🇸', short: 'SR' },
  { code: 'sl', label: 'Slovenščina',    flag: '🇸🇮', short: 'SL' },
  { code: 'sq', label: 'Shqip',          flag: '🇦🇱', short: 'SQ' },
  { code: 'mk', label: 'Македонски',     flag: '🇲🇰', short: 'MK' },
  { code: 'uk', label: 'Українська',     flag: '🇺🇦', short: 'UK' },
  { code: 'ru', label: 'Русский',        flag: '🇷🇺', short: 'RU' },
  { code: 'mt', label: 'Malti',          flag: '🇲🇹', short: 'MT' },
  { code: 'lb', label: 'Lëtzebuergesch', flag: '🇱🇺', short: 'LB' },
  { code: 'tr', label: 'Türkçe',         flag: '🇹🇷', short: 'TR' },
  { code: 'ar', label: 'العربية',        flag: '🇸🇦', short: 'AR', rtl: true },
  { code: 'zh', label: '中文',           flag: '🇨🇳', short: 'ZH' },
  { code: 'ja', label: '日本語',         flag: '🇯🇵', short: 'JA' },
  { code: 'ko', label: '한국어',         flag: '🇰🇷', short: 'KO' },
  { code: 'hi', label: 'हिन्दी',         flag: '🇮🇳', short: 'HI' },
];

const LanguageContext = createContext();

// Nur diese Sprachen sind aktuell final geprüft & auswählbar.
// Alle anderen bleiben sichtbar, aber ausgegraut.
export const ENABLED_LANGS = ['de', 'en', 'hu', 'tr'];

const DEFAULT = 'de';
const STORAGE_KEY = 'stig-lang';

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT;
    const saved = window.localStorage?.getItem(STORAGE_KEY);
    if (saved && ENABLED_LANGS.includes(saved) && translations[saved]) return saved;
    return DEFAULT;
  });

  useEffect(() => {
    try { window.localStorage?.setItem(STORAGE_KEY, lang); } catch (_) { /* ignore */ }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
      const meta = AVAILABLE_LANGS.find((l) => l.code === lang);
      document.documentElement.dir = meta?.rtl ? 'rtl' : 'ltr';
    }
  }, [lang]);

  const [content, setContent] = useState(null);

  useEffect(() => {
    let alive = true;
    // Offline-Bundle (file://): XHR auf lokale Dateien ist im Browser gesperrt,
    // daher wird der Snapshot beim Build als window.__STIG_CONTENT__ eingebettet.
    if (STANDALONE && typeof window !== 'undefined' && window.__STIG_CONTENT__) {
      setContent(window.__STIG_CONTENT__ || {});
      return undefined;
    }
    axios.get(CONTENT_URL)
      .then(({ data }) => { if (alive) setContent(data || {}); })
      .catch(() => { if (alive) setContent({}); });
    return () => { alive = false; };
  }, []);

  const value = useMemo(
    () => {
      let t = translations[lang] || translations[DEFAULT];
      // Admin content overrides apply to the German master only.
      if (lang === 'de' && content && Object.keys(content).length) {
        t = { ...t };
        Object.entries(content).forEach(([slideKey, entry]) => {
          const ov = entry && entry.overrides;
          if (ov && Object.keys(ov).length) {
            t[slideKey] = mergeContent(t[slideKey], ov);
          }
        });
      }
      return {
        lang,
        setLang: (code) => {
          if (ENABLED_LANGS.includes(code) && translations[code]) setLang(code);
        },
        // Legacy toggle: cycles through the enabled languages.
        toggle: () => {
          const codes = ENABLED_LANGS;
          const i = codes.indexOf(lang);
          setLang(codes[(i + 1) % codes.length]);
        },
        t,
        content,
        refreshContent: () => {
          axios.get(CONTENT_URL).then(({ data }) => setContent(data || {})).catch(() => {});
        },
        langs: AVAILABLE_LANGS,
      };
    },
    [lang, content],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useT() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useT must be used within LanguageProvider');
  return ctx;
}
